import React, { useState } from 'react'
import SelectInput2 from '@toasttab/shared/src/components/atoms/Forms/SelectInput/SelectInput2'
import FormField from '@toasttab/shared/src/components/atoms/Forms/FormField/FormField'
import TextInput from '@toasttab/shared/src/components/atoms/Forms/TextInput/TextInput'
import {
  CheckboxInput,
  Button,
  RadioInput,
  RadioInputTile,
  TextArea,
  DateInput2,
  FileInput
} from '@toasttab/shared/src/components'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import classNames from 'classnames'
import { FormStepStatementOptions } from '@toasttab/shared/src/utils/Contentful/richText'
import {
  usePageContext,
  PageContextProps
} from '@toasttab/shared/src/hooks/usePageContext'

export interface Field {
  fieldDependentId?: string
  fieldDependency?: Field[] | null | []
  fieldInputNameMarketoRestApi: string
  fieldLabel: string
  fieldOptions: { key: string; value: string }[] | []
  fieldType: string
  isRequired: boolean
  marketoFieldName: string
  title: string
  __typename: string
}

export type CoreFieldsProps = Field | Statement

export interface Statement {
  title: string
  statement: any
  __typename: string
}

export interface FormStepProps {
  __typename?: string
  color?: 'default' | 'inverse'
  coreFields: Field[] | Statement[]
  description: any
  fieldIdPrefix?: string
  formik: any
  handleSubmit?: any
  heading?: string
  headingList?: any
  heapInteractionSubmit?: string
  isActive?: boolean
  isLastStep?: boolean
  isMultiStepsForm?: boolean
  isProgressiveEligibleForm?: boolean
  isProgressiveForm?: boolean
  isSubmitting?: boolean
  layout?: 'default' | 'inline' | 'twoColumn'
  numSteps?: number
  progressiveFields: Field[]
  submitLabel: string
  title: string
}

const FormStep = ({
  color = 'default',
  coreFields,
  description,
  fieldIdPrefix,
  formik,
  handleSubmit,
  heading,
  headingList,
  heapInteractionSubmit = '',
  isActive,
  isLastStep,
  isProgressiveEligibleForm,
  isProgressiveForm,
  isSubmitting,
  layout = 'default',
  numSteps = 0,
  progressiveFields,
  submitLabel,
  title
}: FormStepProps) => {
  const pageData: PageContextProps = usePageContext() || null
  const pageLP = pageData?.isLP ?? false
  const [validating, setValidating] = useState(false)
  let fieldsetClasses = classNames('', {
    block: layout === 'default' && isActive,
    'flex flex-col md:flex-row gap-4 w-full md:items-center':
      layout === 'inline' && isActive,
    hidden: !isActive
  })
  const render = () => {
    if (isProgressiveEligibleForm) {
      if (isProgressiveForm) {
        return renderFields(progressiveFields)
      } else {
        return renderFields(coreFields)
      }
    } else {
      return renderFields(coreFields)
    }
  }
  const fillOptions = (
    fieldLabel: string,
    options: { key: string; value: string }[]
  ) => {
    let optionArr: { label: string; value: string; disabled: boolean }[] = [
      { label: fieldLabel, value: '', disabled: true }
    ]
    options.map((o: { key: string; value: string }) => {
      return optionArr.push({
        label: o.key,
        value: o.value,
        disabled: false
      })
    })
    return optionArr
  }
  const renderFields = (fields: Field[] | Statement[]): React.JSX.Element[] => {
    return fields?.map((field: any, i: number) => {
      if (field.__typename === 'ContentfulMdsFormField') {
        const f = field as Field

        return (
          <React.Fragment key={i}>
            <FormField fullWidth classes={f.fieldInputNameMarketoRestApi}>
              {f.fieldType === 'Text' && (
                <TextInput
                  type='text'
                  name={f.fieldInputNameMarketoRestApi}
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  placeholder={f.fieldLabel}
                  fullWidth
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation={color}
                  padding='large'
                  required={f.isRequired}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'Email' && (
                <TextInput
                  type='email'
                  name={f.fieldInputNameMarketoRestApi}
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  placeholder={f.fieldLabel}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  fullWidth
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variation={color}
                  padding='large'
                  required={f.isRequired}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'Phone' && (
                <TextInput
                  type='tel'
                  name={f.fieldInputNameMarketoRestApi}
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  placeholder={f.fieldLabel}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  fullWidth
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variation={color}
                  padding='large'
                  required={f.isRequired}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'Date' && (
                <DateInput2
                  min='1900-01-01'
                  max='2100-12-31'
                  name={f.fieldInputNameMarketoRestApi}
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  placeholder={f.fieldLabel}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variation='default'
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'Number' && (
                <TextInput
                  type='number'
                  name={f.fieldInputNameMarketoRestApi}
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  placeholder={f.fieldLabel}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  fullWidth
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variation={color}
                  padding='large'
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'Hidden' && (
                <TextInput
                  type='hidden'
                  name={f.fieldInputNameMarketoRestApi}
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  placeholder={f.fieldLabel}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  fullWidth
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variation={color}
                  padding='large'
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'File' && (
                <FileInput
                  name={f.fieldInputNameMarketoRestApi}
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  placeholder={f.fieldLabel}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variation={color}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'Select (Dropdown)' && (
                <SelectInput2
                  name={f.fieldInputNameMarketoRestApi}
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  options={fillOptions(f.fieldLabel, f.fieldOptions)}
                  fullWidth={true}
                  required={f.isRequired}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  padding='large'
                  variation={color}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'Checkbox (Single/Multi Select)' && (
                <CheckboxInput
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  name={f.fieldInputNameMarketoRestApi}
                  labels={null}
                  label={
                    f.fieldOptions &&
                    f.fieldOptions[0]?.key + (f.isRequired ? '*' : '')
                  }
                  onChange={formik.handleChange}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation='default'
                  checked={
                    formik.values[f.fieldInputNameMarketoRestApi] ?? false
                  }
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
              {f.fieldType === 'Radio (Single-select)' && (
                <RadioInput
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  name={f.fieldInputNameMarketoRestApi}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  options={f.fieldOptions.map(
                    (o: { key: string; value: string }) => ({
                      label: o.key,
                      value: o.value
                    })
                  )}
                  onChange={formik.handleChange}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation='default'
                />
              )}
              {f.fieldType === 'Radio (Single-select Tile)' && (
                <RadioInputTile
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  name={f.fieldInputNameMarketoRestApi}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  options={f.fieldOptions.map(
                    (o: { key: string; value: string }) => ({
                      label: o.key,
                      value: o.value
                    })
                  )}
                  onChange={formik.handleChange}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? null}
                  variation='default'
                  required={f.isRequired}
                />
              )}
              {f.fieldType === 'Text Area' && (
                <TextArea
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  name={f.fieldInputNameMarketoRestApi}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  onChange={formik.handleChange}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation='default'
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                />
              )}
            </FormField>
            {f.fieldDependency?.map((field: any) => {
              const parentFieldValue =
                formik.values[f.fieldInputNameMarketoRestApi] ?? ''
              const dependentFieldValue = field.formFieldValue
              if (
                field.nextFormFieldFormStep &&
                parentFieldValue === dependentFieldValue
              ) {
                return renderFields([field.nextFormFieldFormStep])
              }
              return null
            })}
          </React.Fragment>
        )
      } else if (field.__typename === 'ContentfulMdsStatement') {
        // const f = field as Statement
        return (
          <React.Fragment key={i}>
            <span className=''>
              {renderRichText(field.statement, FormStepStatementOptions)}
            </span>
          </React.Fragment>
        )
      } else {
        return <React.Fragment key={i} />
      }
    })
  }
  const handleNextStep = async () => {
    setValidating(true)
    const errors = await formik.validateForm(formik.values)
    if (Object.keys(errors).length === 0) {
      const formStep = isLastStep ? formik.values.step : formik.values.step + 1
      formik.setValues({ ...formik.values, step: formStep })
    } else {
      formik.setErrors(errors)
    }
    if (isLastStep && validating) {
      if (handleSubmit) {
        handleSubmit()
      } else {
        formik.handleSubmit()
      }
    }
  }

  const formFieldWrapperClasses = classNames(
    'md:grid md:grid-cols-2 md:gap-x-4',
    {
      'md:[&>div:last-child]:col-span-2':
        (!isProgressiveForm && coreFields.length % 2 !== 0) ||
        (isProgressiveForm && progressiveFields?.length % 2 !== 0)
    }
  )

  const headingClasses = classNames({
    'mb-4': pageData?.styleContext !== 'request-demo',
    'mb-1.5': pageData?.styleContext === 'request-demo',
    'text-center': ['fullWidth', 'twoColumn'].includes(layout)
  })

  const headingListIcon = (icon: string) => {
    switch (icon) {
      case 'bullet-gray':
        return 'before:bg-icon-gray-bullet'
      case 'checkmark-gray':
        return 'before:bg-icon-gray-check'
      case 'checkmark-circle-solid-gray':
        return 'before:bg-icon-gray-checkmark-circle'
      case 'checkmark-orange':
        return 'before:bg-icon-orange-check'
      case 'checkmark-circle-solid-orange':
        return 'before:bg-icon-orange-circle-solid-checkmark'
      case 'plus-circle-gray':
        return 'before:bg-icon-gray-plus-circle'
      case 'checkmark-green':
        return 'before:bg-icon-checkmark-green'
      default:
        return ''
    }
  }

  const headingVariation =
    numSteps > 1 || pageLP || ['fullWidth', 'twoColumn'].includes(layout)
      ? 'h3'
      : 'h2'

  return (
    <div className={fieldsetClasses}>
      {numSteps > 1 && !pageLP && (
        <Typography
          variation='overline'
          as='p'
          classes='uppercase text-secondary'
          allowHtml={false}
        >
          STEP {formik.values.step + 1} / {numSteps}
        </Typography>
      )}
      {heading && (
        <Typography as='h3' variation='h3' classes={headingClasses}>
          {heading}
        </Typography>
      )}
      {headingList && headingList.length > 0 && (
        <ul className='list-none mb-3.5'>
          {headingList.map((item: any, i: number) => (
            <li
              key={i}
              className={`flex text-sm text-secondary mb-1 last:mb-none`}
            >
              <span
                className={`mr-2 h-4 w-4 before:mr-1 before:h-4 before:w-4 before:inline-block ${headingListIcon(
                  item.key
                )}`}
              ></span>
              {item.value.content[0].content[0].value}
            </li>
          ))}
        </ul>
      )}
      {title && !heading && (
        <Typography
          as={headingVariation}
          variation={headingVariation}
          classes={headingClasses}
        >
          {title}
        </Typography>
      )}
      {description?.description && (
        <p className='text-gray-100 mb-8'>{description?.description}</p>
      )}
      {layout === 'twoColumn' ? (
        <div className={formFieldWrapperClasses}>{render()}</div>
      ) : (
        render()
      )}
      <Button
        type={isLastStep ? 'submit' : 'button'}
        disabled={isSubmitting}
        arrow={false}
        classes='mt-1 whitespace-pre'
        fullwidth
        size='medium'
        variation={color === 'inverse' ? 'secondary' : 'primary'}
        // heapInteraction={heapInteractionSubmit}
        onClick={handleNextStep}
        heapInteraction={isLastStep ? heapInteractionSubmit : ''}
      >
        {isSubmitting && (
          <div className='absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] '>
            <img
              loading='lazy'
              src='https://toast-craft-assets.s3.amazonaws.com/spinner.gif'
              className='w-[43px] h-[43px] grayscale'
              alt='loadingspinner lazyload'
            />
          </div>
        )}
        <div
          className={`${
            isSubmitting ? 'invisible' : 'font-semibold'
          } leading-none`}
        >
          {submitLabel}
        </div>
      </Button>
    </div>
  )
}

export default FormStep
